import { CheckCircleIcon, CloseIcon, WarningCircleIcon } from "@givenwell/core-solid-icons";
import { useGlobalState } from "@givenwell/hooks";
import { keyframes, styled } from "@givenwell/stitches";
import { colors } from "@givenwell/theme";
import { Toast as ToastPrimitive } from "radix-ui";
import { ReactNode } from "react";
import { createPortal } from "react-dom";
import { dismissToast, Toast, toastsState } from "./Toast";

function getIconForToast(toast: Toast) {
  switch (toast.type) {
    case "log":
      return null;
    case "success":
      return <CheckCircleIcon />;
    case "error":
      return <WarningCircleIcon />;
  }
}

export function Toaster() {
  const [toasts] = useGlobalState(toastsState);

  return (
    <ToastPrimitive.Provider duration={5000} swipeDirection="down" swipeThreshold={10}>
      {toasts.map(toast => {
        const icon = toast.icon || getIconForToast(toast);
        return (
          <Root
            key={toast.id}
            color={toast.type}
            onOpenChange={open => {
              if (!open) {
                setTimeout(() => dismissToast(toast.id), 500);
              }
            }}
          >
            {icon && <Icon>{icon}</Icon>}
            <div>
              <Title>{toast.title}</Title>
              {toast.description && <Description>{toast.description}</Description>}
            </div>

            <Close>
              <CloseIcon width="12" height="12" />
            </Close>
          </Root>
        );
      })}
      <Portal>
        <Viewport />
      </Portal>
    </ToastPrimitive.Provider>
  );
}

function Portal({ children }: { children?: ReactNode }) {
  return createPortal(children, document.body);
}

const VIEWPORT_PADDING = 8;

const Viewport = styled(ToastPrimitive.Viewport, {
  position: "fixed",
  bottom: 0,
  left: 0,
  right: 0,
  pointerEvents: "none",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "flex-end",
  maxWidth: "100vw",
  margin: 0,
  listStyle: "none",
  zIndex: 2147483647,
  outline: "none",
  px: 8,
  pt: 8,
  pb: VIEWPORT_PADDING,
  overflow: "hidden",

  gap: 8,
  mb: "calc(62px + 6px + max(env(safe-area-inset-bottom), 6px))",
  "@sm": {
    mb: `max(0px, env(safe-area-inset-bottom) - ${VIEWPORT_PADDING}px)`,
  },
});

const hide = keyframes({
  "0%": { opacity: 1 },
  "100%": { opacity: 0 },
});

const slideIn = keyframes({
  from: { transform: `translateY(8px)`, opacity: 0 },
  to: { transform: "translateY(0)", opacity: 1 },
});

const swipeOut = keyframes({
  from: { transform: "translateY(var(--radix-toast-swipe-end-y))" },
  to: { transform: `translateY(calc(100% + ${VIEWPORT_PADDING}px))` },
});

const Root = styled(ToastPrimitive.Root, {
  pos: "relative",
  p: 14,
  d: "flex",
  items: "center",
  gap: 10,
  rounded: 24,
  pointerEvents: "auto",
  boxShadow: "0px 0px 9px 0px #00000033, 0px 0px 2px 0px #00000040",

  '&[data-state="open"]': {
    animation: `${slideIn()} 150ms cubic-bezier(0.16, 1, 0.3, 1)`,
  },
  '&[data-state="closed"]': {
    animation: `${hide()} 100ms ease-in`,
  },
  '&[data-swipe="move"]': {
    transform: "translateY(var(--radix-toast-swipe-move-y))",
  },
  '&[data-swipe="cancel"]': {
    transform: "translateX(0)",
    transition: "transform 200ms ease-out",
  },
  '&[data-swipe="end"]': {
    animation: `${swipeOut()} 100ms ease-out`,
  },

  w: "100%",
  "@sm": {
    w: "max-content",
    maxW: 344,
    p: 12,
    rounded: 16,
    gap: 8,
  },

  variants: {
    color: {
      log: {
        bg: colors.blue500,
        color: "white",
      },
      success: {
        bg: colors.green400,
        color: "white",
      },
      error: {
        bg: colors.red500,
        color: "white",
      },
    },
  },
  defaultVariants: {
    color: "log",
  },
});

const Title = styled(ToastPrimitive.Title, {
  fontScale: "md",
  weight: 590,
  lineHeight: "20px",
});

const Description = styled(ToastPrimitive.Description, {
  fontScale: "md",
  mt: 2,
  lineHeight: "20px",
});

const Icon = styled("div", {
  d: "flex",
  justify: "center",
  items: "center",
  size: 38,
  minSize: 38,
  "@sm": {
    size: 32,
    minSize: 32,
  },
});

const Close = styled(ToastPrimitive.Close, {
  pos: "absolute",
  backgroundColor: "inherit",
  size: 20,
  top: -7,
  left: -7,
  rounded: 10,
  items: "center",
  justify: "center",
  boxShadow: "0px 0px 9px 0px #00000033, 0px 0px 2px 0px #00000040",
  opacity: 0,
  transition: "opacity 150ms",
  ":hover > &": {
    opacity: 1,
    transition: "opacity 100ms",
  },
  d: "none",
  "@sm": {
    d: "flex",
  },
});
